import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import {
  ORDER_FILTER_FORM_ID,
  SalesErrorCard,
  SalesParamsForm,
  SalesRatio,
} from '@/components/organisms/Sales';
import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmLongLoading,
  CmScrollBox,
  CmSimpleGrid,
} from '@/components/uiParts';
import {
  ChartDatum,
  createPieDatumFromChartDatum,
  PieDatum,
} from '@/domain/models/Chart';
import {
  AuthStoreRatio,
  DeliveryMethodRatio,
  DeliveryServiceRatio,
  MasterBrandRatio,
  MasterStoreRatio,
} from '@/domain/models/SalesReport/salesReport';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useDeliveryServiceLinkEnterprise } from '@/services/Enterprise/DeliveryService/hooks';
import { useEnterprise } from '@/services/Enterprise/hooks';
import { useSalesRatioFilter } from '@/services/SalesReport/hooks';

type ReportItem =
  | MasterBrandRatio
  | AuthStoreRatio
  | MasterStoreRatio
  | DeliveryServiceRatio
  | DeliveryMethodRatio;

const AggregatesRatesPage: React.FC = () => {
  const history = useHistory();
  const { managerAccountMe: me } = useManagerAccountMe();

  /* Access control */
  redirect404Page('sales_report', history, me);

  const { salesRatio, isLoading, error, filter, setFilter } =
    useSalesRatioFilter();

  if (error) {
    errorHandler({ error });
  }

  const {
    enterprise,
    isLoading: isEnterpriseLoading,
    isError: isEnterpriseError,
  } = useEnterprise();

  const {
    deliveryServices,
    isLoading: isDeliveryServiceLoading,
    isError: isDeliveryServiceError,
  } = useDeliveryServiceLinkEnterprise();

  const createPieChartData = (
    reports: ReportItem[],
    fieldName: string
  ): PieDatum[] => {
    if (!reports || reports.length === 0 || !salesRatio) return [];

    const restTotalSales =
      salesRatio.total_sales -
      reports.reduce((acc, curr) => acc + (curr.total_sales || 0), 0);

    const datum = reports.map((data) => ({
      x: String(data[fieldName as keyof ReportItem]),
      y: data.total_sales || 0,
    }));

    if (restTotalSales > 0) {
      datum.push({
        x: 'その他',
        y: restTotalSales,
      });
    }

    const chartDatum: ChartDatum = {
      id: 'totalPrice',
      data: datum,
    };

    return createPieDatumFromChartDatum(chartDatum);
  };

  const masterBrandDisplayData: PieDatum[] = useMemo(
    () =>
      createPieChartData(
        salesRatio?.master_brands_reports,
        'master_brand_name'
      ),
    [salesRatio]
  );

  const authStoreDisplayData: PieDatum[] = useMemo(
    () =>
      createPieChartData(salesRatio?.auth_stores_reports, 'auth_store_name'),
    [salesRatio]
  );

  const masterStoreDisplayData: PieDatum[] = useMemo(
    () =>
      createPieChartData(
        salesRatio?.master_stores_reports,
        'master_store_name'
      ),
    [salesRatio]
  );

  const deliveryServiceDisplayData: PieDatum[] = useMemo(
    () =>
      createPieChartData(
        salesRatio?.delivery_services_reports,
        'delivery_service_name'
      ),
    [salesRatio]
  );

  const deliveryTypeDisplayData: PieDatum[] = useMemo(
    () =>
      createPieChartData(
        salesRatio?.delivery_methods_reports,
        'delivery_method_name'
      ),
    [salesRatio]
  );

  const ratioData = [
    {
      title: 'ブランド別比率',
      data: masterBrandDisplayData,
    },
    {
      title: '拠点別比率',
      data: authStoreDisplayData,
    },
    {
      title: '店舗別比率',
      data: masterStoreDisplayData,
    },
    {
      title: 'デリバリーサービス別比率',
      data: deliveryServiceDisplayData,
    },
    {
      title: '配達方法別比率',
      data: deliveryTypeDisplayData,
    },
  ] as const;

  const { targetsHeight } = useTargetsDomHeight([
    HEADER_ID,
    ORDER_FILTER_FORM_ID,
  ]);

  return (
    <PageLayout title={routes.SalesRates.title}>
      <CmBox>
        {enterprise && deliveryServices && (
          <SalesParamsForm
            filter={filter}
            enterprise={enterprise}
            deliveryServices={deliveryServices}
            handleSubmit={setFilter}
            orderCount={salesRatio?.total_order_count ?? 0}
            page="rate"
          />
        )}
        <CmScrollBox w="100%" height={`calc(100vh - ${targetsHeight}px)`}>
          <CmBox>
            <CmSimpleGrid
              columns={{ base: 1, md: 2 }}
              spacingX={3}
              spacingY={5}
              my={5}
            >
              {ratioData.map(({ title, data }) => (
                <SalesRatio
                  key={title}
                  title={title}
                  displayData={data}
                  isEmpty={
                    !salesRatio?.total_sales || salesRatio?.total_sales <= 0
                  }
                />
              ))}
            </CmSimpleGrid>
          </CmBox>
        </CmScrollBox>
      </CmBox>

      {(isLoading || isEnterpriseLoading || isDeliveryServiceLoading) && (
        <CmLongLoading />
      )}

      {(isEnterpriseError || isDeliveryServiceError) && <SalesErrorCard />}
    </PageLayout>
  );
};

export default AggregatesRatesPage;
